import { useTranslation } from 'react-i18next';
import Breadcrumbs from '../../coreComponents/breadcrumbs';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  CardText,
  Col,
  Container,
  Form,
  Input,
  Row,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
} from 'reactstrap';
import CommonCardHeader from '../../coreComponents/CommonCardHeader';
import AjaxSourcedData from '../formsAndTable/tables/dataTables/dataSource/AjaxSourcedData';
import { useEffect, useMemo, useRef, useState } from 'react';
import SimpleModal from '../uiKits/modal/basicModal/SimpleModal';
import {
  useLeadsGetAllActiveQuery,
  useLeadsImportMutation,
} from '../../reduxToolkit/reducers/usersSlice';
import { getLeadsTableColumns } from '../../data/formsAndTable/tables/dataTables/DataSource';
import Swal from 'sweetalert2';
import { LeadsNewTabFiltersType, LeadStatus, LeadTab } from '../../types/Leads';
import { CSSObject } from 'styled-components';

import { ConditionalStyles } from 'react-data-table-component';
import { LeadsTableData } from '../../types/Tables';
import Error404 from '../pages/others/errorPage/Error404';

import { NoDataComponent } from '../formsAndTable/forms/formControls/NoDataComponent';
import { getLeadsTabs, LeadsNewTabFilters } from '../../constants';
import { getLeadsCounts } from '../../services/api';
import { LeadsRadioFilters } from './LeadsRadioFilters';
import { LeadsHeadingTooltip } from './components/LeadsHeadingTooltip';

export const LeadsList = () => {
  const { t } = useTranslation();
  const [file, setFile] = useState<File | null>(null);
  const [openFileUpload, setOpenFileUpload] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const [totalRows, setTotalRows] = useState(0);
  const [page, setPage] = useState<number>(1);
  const [perPage, setPerPage] = useState<number>(50);
  const [sort, setSort] = useState<'asc' | 'desc'>('asc');
  const [sortKey, setSortKey] = useState('nextDeadline');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [leadsCounts, setLeadsCounts] = useState<{
    total: number;
    new: number;
    declined: number;
    converted: number;
  }>({
    total: 0,
    new: 0,
    declined: 0,
    converted: 0,
  });
  const [activeTab, setActiveTab] = useState<LeadTab>(getLeadsTabs(t)[0]);
  const [tableLeadStageFilter, setTableLeadStageFilter] = useState<LeadsNewTabFiltersType>(
    LeadsNewTabFilters[0],
  );
  const viewerTz = Intl.DateTimeFormat().resolvedOptions().timeZone;

  useEffect(() => {
    const fetchCounts = async () => {
      try {
        const counts = await getLeadsCounts();
        setLeadsCounts(counts);
      } catch (error) {
        console.error('Failed to fetch leads counts:', error);
      }
    };

    fetchCounts();
  }, []);

  useEffect(() => {
    const id = setTimeout(() => setDebouncedSearch(search.trim()), 400);
    return () => clearTimeout(id);
  }, [search]);
  const [
    handleLeadsImport,
    {
      data: leadsImportData,
      error: leadsImportError,
      isLoading: leadsImportIsLoading,
      isSuccess: leadsImportIsSuccess,
    },
  ] = useLeadsImportMutation();

  const {
    data: activeLeadsData,
    isLoading: activeLeadsIsLoading,
    isSuccess: leadsGetAllActiveSuccess,
    isError,
    isFetching: activeLeadsIsFetching,
  } = useLeadsGetAllActiveQuery(
    {
      page,
      perPage,
      sort,
      sortKey,
      search: debouncedSearch,
      filter: activeTab.value,
      stage: activeTab.value === LeadStatus.NEW ? tableLeadStageFilter.value : undefined,
    },
    {
      skip: leadsCounts?.total === 0,
    },
  );
  useEffect(() => {
    console.log('tableLeadStageFilter.value:', tableLeadStageFilter.value);
  }, [tableLeadStageFilter]);
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFile(e.target.files?.[0] ?? null);
  };
  const handleImportUpload = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!file) return;
    setOpenFileUpload(false);
    try {
      const fd = new FormData();
      fd.append('file', file);

      const res = await handleLeadsImport(fd);
      await new Promise((r) => setTimeout(r, 500));

      // setResult(leadsImportData);
      if (res.data) {
        Swal.fire({
          title: t('leads.importSuccess'),
          html: `<div><p>${t('leads.leadsImported', { qty: res.data.imported })}</p><p>${t('leads.leadsIgnored', { qty: res.data.rejected })}</p></div>`,
          icon: 'success',
          showConfirmButton: true,
          confirmButtonText: t('global.ok'),
        });
      } else {
        Swal.fire({
          title: t('leads.importError'),
          text: t('leads.importErrorText'),
          icon: 'error',
        });
      }
    } catch (err) {
      Swal.fire({
        title: t('leads.importError'),
        text: t('leads.importErrorText'),
        icon: 'error',
      });
    }
  };
  useEffect(() => {
    if (activeLeadsData) {
      setTotalRows(activeLeadsData.total);
      console.log('activeLeadsData:', activeLeadsData);
    }
  }, [activeLeadsData, leadsImportIsSuccess]);
  const handleCancel = () => {
    setFile(null);
    // setResult(null);
    if (fileRef.current) fileRef.current.value = '';
    setOpenFileUpload(false);
  };
  const conditionalRowStyles: ConditionalStyles<LeadsTableData>[] = [
    {
      when: (row: LeadsTableData) => row.status === LeadStatus.CONVERTED,
      style: {
        backgroundColor: 'var(--bs-success-bg-subtle)',
        color: 'var(--bs-success-text-emphasis)',
      } as CSSObject,
    },
    {
      when: (row: LeadsTableData) => row.status === LeadStatus.DECLINED,
      style: {
        backgroundColor: 'var(--bs-danger-bg-subtle)',
        color: 'var(--bs-danger-text-emphasis)',
      } as CSSObject,
    },
  ];

  if (isError) return <Error404 />;
  return (
    <>
      <Breadcrumbs mainTitle={t('leads.sidebarLeadsTitle')} />
      <Container fluid>
        <Card className="position-relative">
          <CardBody>
            <Row>
              {' '}
              <Col sm="8">
                <span className="position-relative">
                  <CommonCardHeader title={t('leads.leadsList')} tag headClass='me-2' />
                  <LeadsHeadingTooltip />
                </span>
              </Col>
              <Col sm="4" className="d-flex justify-content-end">
                {' '}
                <Button onClick={() => setOpenFileUpload(true)}>{t('leads.importLeadsBtn')}</Button>
              </Col>
            </Row>
            <Row className="mt-3">
              <Col sm="12">
                <Nav tabs className="simple-wrapper">
                  {getLeadsTabs(
                    t,
                    leadsCounts?.new,
                    leadsCounts?.declined,
                    leadsCounts?.converted,
                  ).map((item) => (
                    <NavItem key={item.id}>
                      <NavLink
                        tag="button"
                        type="button"
                        className={`txt-info !h4 f-w-500 ${activeTab.id === item.id ? 'active' : ''}`}
                        onClick={() => setActiveTab(item)}
                      >
                        {item.label}
                      </NavLink>
                    </NavItem>
                  ))}
                </Nav>
                <TabContent activeTab={String(activeTab.id)}>
                  {getLeadsTabs(t).map((item) => (
                    <TabPane
                      key={item.id}
                      tabId={String(item.id)}
                      className={`fade ${activeTab.id === item.id ? 'show' : ''}`}
                    >
                      {activeTab.value === LeadStatus.NEW && (
                        <LeadsRadioFilters
                          tableLeadStageFilter={tableLeadStageFilter}
                          setTableLeadStageFilter={setTableLeadStageFilter}
                          t={t}
                        />
                      )}
                      <AjaxSourcedData
                        data={activeLeadsData?.items ?? []}
                        columns={getLeadsTableColumns(t, viewerTz)}
                        pagination={true}
                        paginationServer={true}
                        paginationPerPage={perPage}
                        paginationDefaultPage={1}
                        paginationTotalRows={totalRows}
                        onChangePage={(p) => setPage(p)}
                        onChangeRowsPerPage={(newPerPage, p) => {
                          setPerPage(newPerPage);
                          setPage(1); // or p, depending on your UX
                        }}
                        /* (optional) server sorting */
                        sortServer
                        onSort={(column, direction) => {
                          // column.selector or column.sortField depending on your column defs
                          const key = (column as any).sortField ?? (column as any).selector;
                          setSort(direction);
                          setSortKey(key);
                        }}
                        defaultSortAsc={false}
                        searchServer
                        searchText={search}
                        onSearch={(value) => {
                          setSearch(value);
                          setPage(1);
                        }}
                        conditionalRowStyles={conditionalRowStyles}
                        paginationRowsPerPageOptions={[50, 100, 200]}
                        noDataComponent={<NoDataComponent />}
                        // show inline progress when fetching subsequent pages
                        loading={activeLeadsIsFetching || leadsImportIsLoading}
                      />
                    </TabPane>
                  ))}
                </TabContent>
              </Col>
            </Row>
          </CardBody>
        </Card>
      </Container>
      <SimpleModal isOpen={openFileUpload} close={() => setOpenFileUpload(false)}>
        <Card>
          <CardHeader>
            <CommonCardHeader title={t('leads.import.importLeads')} />
          </CardHeader>
          <CardBody>
            <CardText>
              <h5>{t('leads.import.importLeadsInstructs')}</h5>
              <div className="d-flex flex-column gap-2 mt-3">
                <p className="p-0 m-0">{t('leads.import.importLeadEmailColumn')}</p>
                <p className="p-0 m-0">{t('leads.import.importLeadfullNameColumn')}</p>
                <p className="p-0 m-0">{t('leads.import.importLeadPhoneColumn')}</p>
                <p className="p-0 m-0">{t('leads.import.importLeadRestComlumn')}</p>
              </div>
            </CardText>
            <Form onSubmit={handleImportUpload} encType="multipart/form-data">
              <Input
                innerRef={fileRef}
                type="file"
                onChange={handleFileChange}
                accept=".csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
              />
              <div className="mt-2 d-flex gap-2 mt-3">
                <Button type="submit" color="primary" disabled={!file || leadsImportIsLoading}>
                  {leadsImportIsLoading ? 'Uploading…' : 'Ok'}
                </Button>
                <Button type="button" color="secondary" onClick={handleCancel}>
                  {t('global.cancel')}
                </Button>
              </div>
            </Form>
          </CardBody>
        </Card>
      </SimpleModal>
    </>
  );
};
export default LeadsList;
